/**
 * StrategyArchitect — outlook-driven option-basket builder for the Options AT tab.
 *
 * The user picks an underlying from a watchlist, states a plain-language boundary
 * outlook (won't fall below / won't rise above / will break out of a level) with a
 * horizon + confidence, and the engine returns ranked baskets (reusing the live
 * /options/builder engine). Each result reuses PayoffChartPro and can be executed
 * (persisted as a PENDING basket) with one click.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Select, MenuItem, FormControl, InputLabel, TextField,
  ToggleButton, ToggleButtonGroup, Button, Chip, CircularProgress, Switch, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, Divider, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Collapse, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, ButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import toast from 'react-hot-toast';
import { usePersistedState } from '../../hooks/usePersistedState';
import HelpLink from '../common/HelpLink';
import PayoffChartPro from '../charts/PayoffChartPro';
import BasketReviewDialog from '../charts/BasketReviewDialog';
import { syntheticSymbolsService } from '../../services/syntheticSymbols.service';
import { expiryClock } from '../../utils/optionExpiry';
import { Term, RiskMeter, PlainStatus, useAccountCapital } from './shared/OptionsUi';
import {
  optionsBuilderService, BuilderResponse, BuiltBasket, OutlookMode, Confidence, Objective, TradingMode,
} from '../../services/optionsBuilder.service';

type Sizing = { optInvestmentAmountBuy?: number; optInvestmentAmountSell?: number; leverage?: number };
/** Lots affordable from the underlying's option budget × leverage ÷ this basket's
 *  per-lot margin — the SAME formula the chart "Call Cr" buttons use. Credit baskets
 *  (netPremium < 0) draw on OPT Sell; debit on OPT Buy. */
const autoSizeLots = (b: BuiltBasket, u?: Sizing): number => {
  const isCredit = Number(b.netPremium) < 0;
  const budget = isCredit ? Number(u?.optInvestmentAmountSell || 0) : Number(u?.optInvestmentAmountBuy || 0);
  const lev = u?.leverage && u.leverage > 0 ? u.leverage : 1;
  const perLot = Number(b.marginEstimate) || 0;
  if (!(budget > 0) || !(perLot > 0)) return 1;
  return Math.max(1, Math.floor((budget * lev) / perLot));
};

interface Props {
  /** Watchlists (shared) — { id, name, symbols:[{symbol,exchange}] }. Page mode. */
  watchlists?: any[];
  /** Seed mode (e.g. from the chart): fix the underlying + outlook + level(s),
   *  hide the pickers, and auto-build on mount. */
  seed?: { underlyingSymbol: string; mode: OutlookMode; level: number; level2?: number };
  /** Tighter spacing for embedding in a dialog. */
  compact?: boolean;
  /** Preselect this underlying on open (e.g. "Build a trade" from FNO Intelligence /
   *  Options Lab). Resolves the first watchlist that contains it. */
  defaultSymbol?: string;
  /** Called after a basket is sent to FNO (e.g. to close the chart dialog). */
  onExecuted?: () => void;
}

const MODE_OPTS: { v: OutlookMode; label: string; hint: string }[] = [
  { v: 'WONT_FALL_BELOW', label: "Won't fall below", hint: 'Bullish-to-neutral: you expect a floor to hold.' },
  { v: 'WONT_RISE_ABOVE', label: "Won't rise above", hint: 'Bearish-to-neutral: you expect a ceiling to cap it.' },
  { v: 'WILL_BREAK_ABOVE', label: 'Will break above', hint: 'Bullish breakout: you expect a strong move UP past the level.' },
  { v: 'WILL_BREAK_BELOW', label: 'Will break below', hint: 'Bearish breakout: you expect a strong move DOWN past the level.' },
  { v: 'WILL_BREACH', label: 'Break out (either way)', hint: 'You expect a big move beyond the level — direction can be either way.' },
  { v: 'STAY_IN_RANGE', label: 'Will stay in range', hint: 'Range-bound: you expect it to stay between a lower and an upper level.' },
];
const MODE_LABEL: Record<OutlookMode, string> = MODE_OPTS.reduce((a, m) => { a[m.v] = m.label; return a; }, {} as Record<OutlookMode, string>);
/** Modes that take a second (upper) level. */
const twoLevel = (m: OutlookMode) => m === 'WILL_BREACH' || m === 'STAY_IN_RANGE';
const fmtInr = (n: number, p = 0) => '₹' + (Math.round(n * 10 ** p) / 10 ** p).toLocaleString('en-IN', { maximumFractionDigits: p });
const FIT_HELP = 'Fit score (0–100): the overall ranking of this basket for YOUR outlook. It blends — most heavily — whether you actually make money when your view is proven (the "thesis" test), plus win probability, expected P&L, reward:risk, the Greeks posture (e.g. positive theta for income, long gamma/vega for breakouts), IV-regime fit, and liquidity. Higher = better all-round fit; baskets that lose when you are right are pushed down.';
const PAPER_HELP = 'Places this basket as a SIMULATED position (filled at the legs’ last price — no broker order). It is monitored under Trades → Option baskets exactly like a live one (auto TP/SL/expiry), tagged PAPER, so you can track the outcome risk-free.';
const LIVE_HELP = 'Places REAL orders on the exchange via your broker (protective BUY legs first), through the kill-switch / daily-loss / margin gates. You’ll confirm the legs, quantity, margin and max-loss first. Monitored under Trades → Option baskets, tagged LIVE.';
const shapeColor: Record<string, 'success' | 'info' | 'warning'> = { credit: 'success', debit: 'info', undefined: 'warning' };
const fitColor = (s: number) => (s >= 70 ? '#2e7d32' : s >= 45 ? '#ed6c02' : '#90a4ae');

/** Operative profitable spot range from the payoff-at-expiry curve. */
const profitRangeText = (b: BuiltBasket, base: string): string | null => {
  const xs = b.payoffData?.spotRange || [], ys = b.payoffData?.expiry || [];
  if (!xs.length || !ys.length) return null;
  const dMin = xs[0], dMax = xs[xs.length - 1];
  const zones: Array<[number, number]> = [];
  let start: number | null = null;
  for (let i = 0; i < xs.length; i++) {
    const ok = Number(ys[i]) >= 0;
    if (ok && start === null) start = xs[i];
    else if (!ok && start !== null) { zones.push([start, xs[i]]); start = null; }
  }
  if (start !== null) zones.push([start, xs[xs.length - 1]]);
  if (!zones.length) return null;
  const fmt = (v: number) => Math.round(v).toLocaleString('en-IN');
  return base + ' ' + zones.map(([lo, hi]) => {
    const tLo = lo <= dMin + 1e-6, tHi = hi >= dMax - 1e-6;
    if (tLo && tHi) return 'any level';
    if (tLo) return `below ${fmt(hi)}`;
    if (tHi) return `above ${fmt(lo)}`;
    return `${fmt(lo)}–${fmt(hi)}`;
  }).join(' or ');
};

const StrategyArchitect: React.FC<Props> = ({ watchlists = [], seed, compact, defaultSymbol, onExecuted }) => {
  const [wlId, setWlId] = usePersistedState<number | ''>('architect.wl', '');
  const [pageSymbol, setSymbol] = usePersistedState<string>('architect.symbol', '');
  const [pageMode, setMode] = usePersistedState<OutlookMode>('architect.mode', 'WONT_FALL_BELOW');
  const [level, setLevel] = useState<string>(seed ? String(seed.level) : '');
  const [level2, setLevel2] = useState<string>(seed?.level2 != null ? String(seed.level2) : '');
  // When seeded (chart), the underlying + outlook are fixed by the seed.
  const symbol = seed ? seed.underlyingSymbol : pageSymbol;
  const mode = seed ? seed.mode : pageMode;
  const [horizonValue, setHorizonValue] = usePersistedState<number>('architect.hv', 5);
  const [horizonUnit, setHorizonUnit] = usePersistedState<'hours' | 'days'>('architect.hu', 'days');
  const [confidence, setConfidence] = usePersistedState<Confidence>('architect.conf', 'medium');
  const [maxLegs, setMaxLegs] = usePersistedState<number>('architect.maxLegs', 4);
  const [topX, setTopX] = usePersistedState<number>('architect.topX', 6);
  const [objective, setObjective] = usePersistedState<Objective>('architect.obj', 'balanced');
  const [includeNaked, setIncludeNaked] = usePersistedState<boolean>('architect.naked', false);

  const [view, setView] = usePersistedState<'cards' | 'grid'>('architect.view', 'cards');
  // Simple (guided) by default — hides the expert dials (objective / legs / count /
  // undefined-risk) behind an "Advanced" toggle so a beginner isn't overwhelmed.
  const [showAdvanced, setShowAdvanced] = usePersistedState<boolean>('architect.advanced', false);
  const [loading, setLoading] = useState(false);
  // Persist the last result in PAGE mode so navigating away & back doesn't lose it;
  // the seeded (chart dialog) instance is ephemeral and rebuilds each open.
  const [respSeed, setRespSeed] = useState<BuilderResponse | null>(null);
  const [respPage, setRespPage] = usePersistedState<BuilderResponse | null>('architect.resp.v1', null);
  const resp = seed ? respSeed : respPage;
  const setResp = seed ? setRespSeed : setRespPage;
  // placing key = `${basket.id}:${mode}` while a place call is in flight.
  const [placing, setPlacing] = useState<string | null>(null);
  // LIVE placement opens a confirm dialog first (legs / qty / margin / max-loss).
  const [liveConfirm, setLiveConfirm] = useState<{ b: BuiltBasket; lots: number } | null>(null);
  // Item 10 — the basket currently open in the editable review dialog (adjust wings/expiry).
  const [adjust, setAdjust] = useState<BuiltBasket | null>(null);
  // Stable legs reference for the dialog's preview effect (avoid re-preview loop).
  const adjustLegs = useMemo(
    () => (adjust ? adjust.legs.map(l => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike), expiry: l.expiry })) : null),
    [adjust],
  );

  // Underlyings from the chosen watchlist (options live on Delta/NFO).
  const symbols: string[] = useMemo(() => {
    const wl = watchlists.find(w => w.id === wlId);
    return Array.from(new Set(((wl?.symbols || []) as any[]).map(s => s.symbol))).sort() as string[];
  }, [watchlists, wlId]);

  // "Build a trade for <symbol>" preselect (C6): resolve the first watchlist that holds the
  // requested underlying and select it. Applied once per distinct defaultSymbol so it never
  // fights the user's own picks afterwards.
  const appliedDefault = useRef<string | null>(null);
  useEffect(() => {
    const ds = (defaultSymbol || '').trim();
    if (seed || !ds || appliedDefault.current === ds || !watchlists.length) return;
    const wl = watchlists.find(w => (w.symbols || []).some((s: any) => String(s.symbol).toUpperCase() === ds.toUpperCase()));
    if (!wl) return;
    const exact = (wl.symbols || []).find((s: any) => String(s.symbol).toUpperCase() === ds.toUpperCase());
    setWlId(wl.id);
    setSymbol(exact ? exact.symbol : ds);
    appliedDefault.current = ds;
  }, [defaultSymbol, watchlists, seed, setWlId, setSymbol]);

  const run = async () => {
    if (!symbol) { toast.error('Pick an underlying'); return; }
    const L = Number(level), L2 = Number(level2);
    if (!(L > 0)) { toast.error(mode === 'STAY_IN_RANGE' ? 'Enter the lower level' : 'Enter the price level for your outlook'); return; }
    if (mode === 'STAY_IN_RANGE' && !(L2 > 0 && L2 !== L)) { toast.error('A range outlook needs both a lower and an upper level'); return; }
    setLoading(true); setResp(null);
    const r = await optionsBuilderService.build({
      underlyingSymbol: symbol,
      outlook: { mode, level: L, level2: twoLevel(mode) && L2 > 0 ? L2 : undefined, horizonValue, horizonUnit, confidence },
      maxLegs, topX, objective, includeUndefinedRisk: includeNaked,
    });
    setResp(r);
    setLoading(false);
    if (!r.ok) toast.error(r.reason || 'No baskets', { duration: 6000 });
  };
  const runRef = React.useRef(run); runRef.current = run;
  // Seeded (chart) → auto-build once on mount.
  React.useEffect(() => { if (seed) runRef.current(); /* eslint-disable-next-line */ }, []);

  // Place a basket directly: PAPER is one-click; LIVE routes through the confirm
  // dialog first (opened by onPlace) and then lands here.
  const doPlace = async (b: BuiltBasket, lots: number, mode: TradingMode) => {
    setPlacing(`${b.id}:${mode}`);
    const r = await optionsBuilderService.place(symbol, b, lots, mode);
    setPlacing(null);
    if (r.ok) {
      toast.success(`${mode === 'PAPER' ? '📝 Paper' : '🟢 LIVE'} basket placed — "${b.name}" ×${lots}. Track it under Trades → Option baskets (auto TP/SL).`, { duration: 8000 });
      setLiveConfirm(null);
      onExecuted?.();
    } else {
      toast.error(r.gates?.length ? `Blocked: ${r.gates.map(g => g.message).join('; ')}` : (r.reason || 'Place failed'), { duration: 9000 });
    }
  };
  /** Persist a PROPOSED basket as a synthetic symbol (no order placed). It then shows up in
   *  the Charts symbol pane under "Synthetic · saved" for this underlying, chartable and
   *  tradable via its own BUY/SHORT premium triggers. */
  const [savingSym, setSavingSym] = useState<string | null>(null);
  const onSaveSymbol = async (b: BuiltBasket) => {
    setSavingSym(b.id);
    const viewAligned =
      seed?.mode === 'WONT_FALL_BELOW' || seed?.mode === 'WILL_BREAK_ABOVE' ? 'UP'
      : seed?.mode === 'WONT_RISE_ABOVE' || seed?.mode === 'WILL_BREAK_BELOW' ? 'DOWN'
      : 'NEUT';
    const r = await syntheticSymbolsService.create({
      underlyingSymbol: symbol,
      strategyId: b.id,
      legs: b.legs.map(l => ({ action: l.action, optionType: l.optionType, strike: l.strike, expiry: l.expiry })),
      netPremium: Number(b.netPremium) || 0,
      viewAligned,
    });
    setSavingSym(null);
    if (r.ok) {
      toast.success(`💾 Saved as synthetic symbol “${r.name}” — find it in the Charts symbol pane.`, { duration: 7000, id: 'synth-save' });
      try { window.dispatchEvent(new Event('synthetics-changed')); } catch { /* noop */ }
    } else {
      toast.error(r.reason || 'Could not save as a synthetic symbol', { id: 'synth-save' });
    }
  };

  const onPlace = (b: BuiltBasket, lots: number, mode: TradingMode) => {
    if (mode === 'LIVE') setLiveConfirm({ b, lots });
    else doPlace(b, lots, 'PAPER');
  };

  const u = resp?.underlying;
  const modeHint = MODE_OPTS.find(m => m.v === mode)?.hint;

  return (
    <Box>
      {/* ── Input panel ─────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {!compact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AutoFixHighIcon color="primary" fontSize="small" />
              <Typography sx={{ fontWeight: 800 }}>Strategy Architect</Typography>
              <Tooltip arrow title="Tell the engine your view on the underlying and it builds + ranks the best option baskets (defined-risk by default) from live option data.">
                <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
              <Box sx={{ ml: 'auto' }}><HelpLink to="options-at" label="How it works" /></Box>
            </Box>
          )}

          {seed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Chip size="small" color="primary" label={seed.underlyingSymbol} sx={{ fontWeight: 800 }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{MODE_LABEL[mode]} ₹{seed.level.toLocaleString('en-IN')}{seed.level2 != null ? `–₹${seed.level2.toLocaleString('en-IN')}` : ''}</Typography>
            </Box>
          )}

          {!seed && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <FormControl size="small">
                <InputLabel>Watchlist</InputLabel>
                <Select label="Watchlist" value={wlId} onChange={e => { setWlId(e.target.value as number); setSymbol(''); }}>
                  {watchlists.length === 0 && <MenuItem value="" disabled>No watchlists</MenuItem>}
                  {watchlists.map(w => <MenuItem key={w.id} value={w.id}>{w.name} ({(w.symbols || []).length})</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Underlying</InputLabel>
                <Select label="Underlying" value={symbol} onChange={e => setSymbol(String(e.target.value))}>
                  {symbols.length === 0 && <MenuItem value="" disabled>Pick a watchlist first</MenuItem>}
                  {symbols.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {!seed && <Divider sx={{ my: 1.5 }} />}

          {/* Outlook */}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>My outlook — over the next…</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 0.75 }}>
            {!seed && (
              <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
                {MODE_OPTS.map(m => <ToggleButton key={m.v} value={m.v} sx={{ textTransform: 'none', px: 1.25 }}>{m.label}</ToggleButton>)}
              </ToggleButtonGroup>
            )}
            <TextField size="small" label={mode === 'STAY_IN_RANGE' ? 'Lower level' : twoLevel(mode) ? 'Level' : 'Price level'} value={level} onChange={e => setLevel(e.target.value)}
              type="number" sx={{ width: 130 }} placeholder="e.g. 95000" />
            {twoLevel(mode) && (
              <TextField size="small" label={mode === 'STAY_IN_RANGE' ? 'Upper level' : 'Upper level (optional)'} value={level2} onChange={e => setLevel2(e.target.value)} type="number" sx={{ width: 160 }} />
            )}
            <TextField size="small" label="Horizon" value={horizonValue} onChange={e => setHorizonValue(Math.max(1, Number(e.target.value) || 1))} type="number" sx={{ width: 90 }} />
            <ToggleButtonGroup size="small" exclusive value={horizonUnit} onChange={(_, v) => v && setHorizonUnit(v)}>
              <ToggleButton value="hours" sx={{ textTransform: 'none', px: 1.25 }}>Hours</ToggleButton>
              <ToggleButton value="days" sx={{ textTransform: 'none', px: 1.25 }}>Days</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {modeHint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{modeHint}</Typography>}

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>How sure are you?</Typography>
              <ToggleButtonGroup size="small" exclusive value={confidence} onChange={(_, v) => v && setConfidence(v)}>
                <ToggleButton value="poor" sx={{ textTransform: 'none', px: 1.25 }}>Not very</ToggleButton>
                <ToggleButton value="medium" sx={{ textTransform: 'none', px: 1.25 }}>Fairly</ToggleButton>
                <ToggleButton value="high" sx={{ textTransform: 'none', px: 1.25 }}>Very</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Button size="small" variant="text" onClick={() => setShowAdvanced(a => !a)} sx={{ textTransform: 'none' }}>
              {showAdvanced ? '− Fewer options' : '+ Advanced options'}
            </Button>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />} disabled={loading} onClick={run} sx={{ ml: 'auto' }}>
              {loading ? 'Building…' : 'Show me trades'}
            </Button>
          </Box>

          {/* Advanced dials — hidden by default so beginners see only view + level + horizon. */}
          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Optimise for</InputLabel>
                <Select label="Optimise for" value={objective} onChange={e => setObjective(e.target.value as Objective)}>
                  <MenuItem value="balanced">Balanced (recommended)</MenuItem>
                  <MenuItem value="winrate">Highest win-rate</MenuItem>
                  <MenuItem value="ev">Highest expected return</MenuItem>
                  <MenuItem value="rr">Best reward:risk</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" label="Max legs" value={maxLegs} onChange={e => setMaxLegs(Math.max(1, Math.min(6, Number(e.target.value) || 4)))} type="number" sx={{ width: 100 }} />
              <TextField size="small" label="How many results" value={topX} onChange={e => setTopX(Math.max(1, Math.min(20, Number(e.target.value) || 6)))} type="number" sx={{ width: 130 }} />
              <FormControlLabel sx={{ ml: 0 }} control={<Switch checked={includeNaked} onChange={e => setIncludeNaked(e.target.checked)} />}
                label={<Typography variant="caption">Include <Term k="undefinedRisk">undefined-risk</Term> structures</Typography>} />
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* ── Context strip ───────────────────────────────────────────────── */}
      {u && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip size="small" label={`Spot ₹${u.spot.toLocaleString('en-IN')}`} />
          <Chip size="small" label={`Expiry ${u.expiry}`} />
          <Chip size="small" label={`ATM ${u.atmStrike}`} />
          <Chip size="small" color={u.ivRank >= 60 ? 'warning' : u.ivRank <= 30 ? 'info' : 'default'} label={`IV Rank ${u.ivRank.toFixed(0)} (${u.ivRank >= 60 ? 'rich' : u.ivRank <= 30 ? 'cheap' : 'mid'})`} />
          {resp?.consideredCount != null && <Chip size="small" variant="outlined" label={`${resp.baskets.length} of ${resp.consideredCount} considered`} />}
        </Box>
      )}

      {/* ── Results ─────────────────────────────────────────────────────── */}
      {resp?.ok && resp.notice && (
        <Alert severity="warning" sx={{ mb: 2 }}>{resp.notice}</Alert>
      )}
      {resp && !resp.ok && (
        <Alert severity="info" sx={{ mb: 2 }}>{resp.reason || 'No baskets found.'}</Alert>
      )}
      {resp?.ok && resp.baskets.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>No baskets matched your outlook + constraints. Try widening Max legs, lowering confidence, or enabling advanced structures.</Alert>
      )}

      {resp?.ok && resp.baskets.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Ranked solutions</Typography>
          <Tooltip arrow title="“If view just proven” = your P&L if price merely holds your level at expiry (no further move). Baskets are ranked to make money when you're right — not only when price moves a lot.">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
              <InfoOutlinedIcon sx={{ fontSize: 13 }} /> ranked to profit when your view is proven
            </Typography>
          </Tooltip>
          <Box sx={{ ml: 'auto' }}>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
              <ToggleButton value="cards" sx={{ px: 1 }}><Tooltip title="Card view" arrow><ViewModuleIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
              <ToggleButton value="grid" sx={{ px: 1 }}><Tooltip title="Grid view (sortable)" arrow><ViewListIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {view === 'cards' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {resp?.baskets.map((b, i) => (
            <BasketCard key={b.id + i} b={b} rank={i + 1} spot={u?.spot || 0} sizing={u} base={u?.symbol || symbol} feedSource={u?.feedSource} placing={placing} onPlace={onPlace} onAdjust={setAdjust} onSaveSymbol={onSaveSymbol} savingSym={savingSym} />
          ))}
        </Box>
      )}
      {view === 'grid' && resp?.ok && resp.baskets.length > 0 && (
        <BasketGrid baskets={resp.baskets} spot={u?.spot || 0} sizing={u} base={u?.symbol || symbol} placing={placing} onPlace={onPlace} onAdjust={setAdjust} onSaveSymbol={onSaveSymbol} savingSym={savingSym} />
      )}

      {/* LIVE placement confirm — explicit sign-off with legs / qty / margin / max-loss. */}
      <LiveConfirmDialog
        open={!!liveConfirm}
        b={liveConfirm?.b || null}
        lots={liveConfirm?.lots || 1}
        symbol={symbol}
        busy={!!liveConfirm && placing === `${liveConfirm.b.id}:LIVE`}
        onCancel={() => setLiveConfirm(null)}
        onConfirm={() => liveConfirm && doPlace(liveConfirm.b, liveConfirm.lots, 'LIVE')}
      />

      {/* Item 10 — per-basket "Adjust & place": opens the SAME editable review dialog
          the chart action buttons use, so the user can move wing strikes (distance)
          and push the protective BUY legs to a later expiry, then re-price & place. */}
      <BasketReviewDialog
        open={!!adjust}
        underlyingSymbol={symbol}
        strategyId={adjust?.id || null}
        strategyLabel={adjust?.name || 'Basket'}
        spotPrice={u?.spot || 0}
        initialLegs={adjustLegs}
        sellInvestmentInr={u?.optInvestmentAmountSell}
        buyInvestmentInr={u?.optInvestmentAmountBuy}
        leverage={u?.leverage}
        onClose={() => setAdjust(null)}
        onExecuted={() => { setAdjust(null); onExecuted?.(); }}
      />
    </Box>
  );
};

// ─────────────────────────── LIVE placement confirm ───────────────────────────
const LiveConfirmDialog: React.FC<{ open: boolean; b: BuiltBasket | null; lots: number; symbol: string; busy: boolean; onCancel: () => void; onConfirm: () => void }> = ({ open, b, lots, symbol, busy, onCancel, onConfirm }) => {
  if (!b) return null;
  const margin = (b.marginEstimate || 0) * lots;
  const maxLoss = (b.maxLoss || 0) * lots;
  const maxProfit = (b.maxProfit || 0) * lots;
  return (
    <Dialog open={open} onClose={() => { if (!busy) onCancel(); }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>Place LIVE on exchange?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 1.5 }}>This places <strong>real orders</strong> on {symbol} via your broker (protective BUY legs first), subject to the kill-switch / daily-loss / margin gates.</Alert>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>{b.name} · ×{lots} {lots === 1 ? 'lot' : 'lots'}</Typography>
        {[...b.legs].sort((a, b2) => b2.strike - a.strike).map((l, i) => (
          <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
            {l.action === 'BUY' ? '＋' : '－'} {l.action} {l.optionType} {l.strike} @ {Number(l.ltp || 0).toFixed(2)} <span style={{ opacity: 0.6 }}>{l.expiry}</span>
          </Typography>
        ))}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, mt: 1.5 }}>
          <Metric label="Est. margin" value={fmtInr(margin)} />
          <Metric label="Max profit" value={fmtInr(maxProfit)} color="#2e7d32" />
          <Metric label="Max loss" value={fmtInr(maxLoss)} color="#c62828" />
        </Box>
        {/* Net-debit structures: you pay up front, loss is capped, and the auto-stop is
            held off for the first few minutes so the entry spread can't insta-close it. */}
        {Number(b.netPremium) >= 0 && (
          <Alert severity="info" icon={false} sx={{ mt: 1.5, py: 0.5, fontSize: '0.78rem' }}>
            ℹ️ You <strong>pay {fmtInr(Math.abs((b.netPremium || 0) * lots))} up front</strong> for this (a debit).
            Your loss is <strong>capped at {fmtInr(maxLoss)}</strong> — you can't lose more. The auto stop-loss stays
            off for the first few minutes after placing, so the entry bid/ask spread can't square it off instantly.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="error" disabled={busy} onClick={onConfirm} startIcon={busy ? <CircularProgress size={15} color="inherit" /> : undefined}>
          {busy ? 'Placing…' : 'Place LIVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BasketCard: React.FC<{ b: BuiltBasket; rank: number; spot: number; sizing?: Sizing; base?: string; feedSource?: string; placing: string | null; onPlace: (b: BuiltBasket, lots: number, mode: TradingMode) => void; onAdjust?: (b: BuiltBasket) => void; onSaveSymbol?: (b: BuiltBasket) => void; savingSym?: string | null }> = ({ b, rank, spot, sizing, base, feedSource, placing, onPlace, onAdjust, onSaveSymbol, savingSym }) => {
  const [lots, setLots] = useState<number>(() => autoSizeLots(b, sizing));
  const capital = useAccountCapital();
  const m = lots;
  const provenPnl = b.provenPnl * m, expectedPnl = b.expectedPnl * m, maxProfit = b.maxProfit * m, maxLoss = b.maxLoss * m;
  const evPos = expectedPnl >= 0;
  const isCredit = Number(b.netPremium) < 0;
  const autoNote = sizing && (isCredit ? Number(sizing.optInvestmentAmountSell || 0) > 0 : Number(sizing.optInvestmentAmountBuy || 0) > 0)
    ? `auto-sized from OPT ${isCredit ? 'Sell' : 'Buy'} budget × ${sizing.leverage && sizing.leverage > 0 ? sizing.leverage : 1}× lev`
    : null;
  const payoff = useMemo(() => ({
    ...b,
    payoffData: b.payoffData ? {
      ...b.payoffData,
      expiry: (b.payoffData.expiry || []).map(v => v * m),
      today: (b.payoffData.today || []).map(v => v * m),
      ...(b.payoffData.far ? { far: b.payoffData.far.map(v => v * m) } : {}),
    } : b.payoffData,
  }), [b, m]);
  const placingThis = placing === `${b.id}:PAPER` || placing === `${b.id}:LIVE`;
  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${fitColor(b.fitScore)}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Chip size="small" label={`#${rank}`} sx={{ height: 20, fontWeight: 800 }} />
          <Typography sx={{ fontWeight: 800 }}>{b.name}</Typography>
          <Chip size="small" color={shapeColor[b.riskShape]} label={b.riskShape} sx={{ height: 18, fontSize: '0.58rem', textTransform: 'capitalize' }} />
          <Tooltip arrow title={FIT_HELP}>
            <Chip size="small" label={`Fit ${b.fitScore}`} sx={{ ml: 'auto', height: 20, fontWeight: 800, bgcolor: fitColor(b.fitScore), color: '#fff', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary">{b.category} · {b.rationale}</Typography>

        {/* Plain-English headline — the one sentence a beginner reads first. */}
        {(() => {
          const rangeText = (profitRangeText(b, '') || '').trim();
          const tone: 'good' | 'neutral' = provenPnl >= 0 ? 'good' : 'neutral';
          return (
            <Box sx={{ mt: 0.75 }}>
              <PlainStatus tone={tone} emoji={provenPnl >= 0 ? '✅' : '💡'}>
                {rangeText ? `You profit if ${base || 'the price'} finishes ${rangeText}. ` : ''}
                Risk up to ₹{Math.abs(Math.round(maxLoss)).toLocaleString('en-IN')} to make up to ₹{Math.round(maxProfit).toLocaleString('en-IN')}
                {b.winProbability != null ? ` · about ${b.winProbability}% estimated chance of profit.` : '.'}
              </PlainStatus>
            </Box>
          );
        })()}

        {/* Key metrics (scaled by lots) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, my: 1 }}>
          <Metric label={<Term k="thesis">If view just proven</Term>} value={`${provenPnl >= 0 ? '+' : '−'}₹${Math.abs(Math.round(provenPnl)).toLocaleString('en-IN')}`} color={provenPnl >= 0 ? '#2e7d32' : '#c62828'} />
          <Metric label={<Term k="winProbability">Win chance</Term>} value={`${b.winProbability}%`} />
          <Metric label={<Term k="expectedPnl">Exp. P&L</Term>} value={fmtInr(expectedPnl)} color={evPos ? '#2e7d32' : '#c62828'} />
          <Metric label={<Term k="maxProfit">Max profit</Term>} value={fmtInr(maxProfit)} color="#2e7d32" />
          <Metric label={<Term k="maxLoss">Max loss</Term>} value={fmtInr(maxLoss)} color="#c62828" />
          <Metric label={<Term k="margin">Margin req.</Term>} value={fmtInr((b.marginEstimate || 0) * m)} />
          <Metric label={<Term k="rewardRisk">Reward:Risk</Term>} value={Math.abs(maxLoss) > 0 && maxProfit > 0 ? `${Math.abs(maxProfit / maxLoss).toFixed(2)}:1` : '—'} />
        </Box>
        {(() => {
          // Expiry-by (the near/short leg settles first) + operative profitable range.
          const nearExp = b.legs.find(l => l.action === 'SELL')?.expiry || b.legs[0]?.expiry;
          const clk = expiryClock(nearExp, feedSource);
          const range = profitRangeText(b, base || 'spot');
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, mt: 0.3 }}>
              {clk && (
                <Typography variant="caption" color="text.secondary">
                  Expires <strong>{clk.label}</strong>{!clk.expired && <Box component="span" sx={{ color: '#ed6c02', fontWeight: 700 }}> · {clk.dte} left</Box>}
                </Typography>
              )}
              {range && (
                <Typography variant="caption" color="text.secondary">
                  Profitable: <strong style={{ color: '#2e7d32' }}>{range}</strong>
                  {b.breakEvens.length > 0 && <> · BE {b.breakEvens.map(x => Math.round(x).toLocaleString('en-IN')).join(' / ')}</>}
                </Typography>
              )}
              {!range && b.breakEvens.length > 0 && (
                <Typography variant="caption" color="text.secondary">Break-even: <strong>{b.breakEvens.map(x => x.toLocaleString('en-IN')).join(' / ')}</strong></Typography>
              )}
            </Box>
          );
        })()}

        {/* Risk in context — ₹ and % of your total capital. */}
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Most you can lose</Typography>
          <RiskMeter riskInr={Math.abs(maxLoss)} capitalInr={capital ?? undefined} compact />
        </Box>

        {/* Payoff (scaled by lots) */}
        <Box sx={{ mt: 1 }}>
          <PayoffChartPro basket={payoff as any} spotPrice={spot} underlyingSymbol={base} />
        </Box>

        {/* Why this — the analysis */}
        {b.reasoning?.length > 0 && (
          <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase', mb: 0.3 }}>Why this</Typography>
            {b.reasoning.map((line, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.5, color: i === 0 ? 'text.primary' : 'text.secondary' }}>
                {i === 0 ? '' : '• '}{line}
              </Typography>
            ))}
          </Box>
        )}

        {/* Legs + greeks */}
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 30, p: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{b.legs.length} legs · Δ {b.greeks.delta} · Γ {b.greeks.gamma} · θ {b.greeks.theta} · ν {b.greeks.vega}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, pt: 0.5 }}>
            {[...b.legs].sort((a, b2) => b2.strike - a.strike).map((l, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                {l.action === 'BUY' ? '＋' : '－'} {l.action} {l.optionType} {l.strike} @ {Number(l.ltp || 0).toFixed(2)} <span style={{ opacity: 0.6 }}>{l.expiry}</span>
              </Typography>
            ))}
            {b.cvar5 < 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Worst-5% outcome ≈ {fmtInr(b.cvar5)}</Typography>}
          </AccordionDetails>
        </Accordion>

        {b.warnings.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {b.warnings.map((w, i) => <Typography key={i} variant="caption" sx={{ display: 'block', color: 'warning.main' }}>⚠ {w}</Typography>)}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <Box>
            <TextField size="small" label="Qty (lots)" type="number" value={lots}
              onChange={e => setLots(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
              sx={{ width: 100 }} inputProps={{ min: 1 }} />
            {autoNote && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontSize: '0.6rem' }}>{autoNote}</Typography>}
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            {onAdjust && (
              <Tooltip arrow title="Open the editable review dialog — move wing strikes (distance), push the protective BUY legs to a later expiry, re-price, then place.">
                <span><Button size="small" variant="text" disabled={placingThis} onClick={() => onAdjust(b)}>✎ Adjust</Button></span>
              </Tooltip>
            )}
            {onSaveSymbol && (
              <Tooltip arrow title="Save this basket as a SYNTHETIC SYMBOL (no order placed). It appears in the Charts symbol pane for this underlying, where you can chart its combined premium and arm BUY/SHORT triggers on it.">
                <span><Button size="small" variant="outlined" color="secondary" disabled={savingSym === b.id} onClick={() => onSaveSymbol(b)}>
                  {savingSym === b.id ? 'Saving…' : '💾 Save as symbol'}
                </Button></span>
              </Tooltip>
            )}
            <Tooltip arrow title={PAPER_HELP}>
              <span><Button size="small" variant="outlined" color="info" disabled={placingThis} onClick={() => onPlace(b, lots, 'PAPER')}>
                {placing === `${b.id}:PAPER` ? 'Placing…' : '📝 Paper trade'}
              </Button></span>
            </Tooltip>
            <Tooltip arrow title={LIVE_HELP}>
              <span><Button size="small" variant="contained" color="error" disabled={placingThis} onClick={() => onPlace(b, lots, 'LIVE')}>
                {placing === `${b.id}:LIVE` ? 'Placing…' : '🟢 Place LIVE'}
              </Button></span>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Metric: React.FC<{ label: React.ReactNode; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography component="div" sx={{ fontSize: '0.56rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

// ─────────────────────────── Grid (sortable) view ───────────────────────────
type SortKey = 'fitScore' | 'winProbability' | 'expectedPnl' | 'provenPnl' | 'maxProfit' | 'maxLoss' | 'marginEstimate' | 'name';
const BasketGrid: React.FC<{ baskets: BuiltBasket[]; spot: number; sizing?: Sizing; base?: string; placing: string | null; onPlace: (b: BuiltBasket, lots: number, mode: TradingMode) => void; onAdjust?: (b: BuiltBasket) => void; onSaveSymbol?: (b: BuiltBasket) => void; savingSym?: string | null }> = ({ baskets, spot, sizing, base, placing, onPlace, onAdjust, onSaveSymbol, savingSym }) => {
  const [sortKey, setSortKey] = useState<SortKey>('fitScore');
  const [asc, setAsc] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [lotsByRow, setLotsByRow] = useState<Record<string, number>>({});
  const lotsFor = (b: BuiltBasket) => lotsByRow[b.id] ?? autoSizeLots(b, sizing);
  const rows = useMemo(() => {
    const arr = [...baskets];
    arr.sort((a, b) => {
      if (sortKey === 'name') return asc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      const av = (a as any)[sortKey] ?? 0, bv = (b as any)[sortKey] ?? 0;
      return asc ? av - bv : bv - av;
    });
    return arr;
  }, [baskets, sortKey, asc]);
  const toggleSort = (k: SortKey) => { if (sortKey === k) setAsc(!asc); else { setSortKey(k); setAsc(k === 'name' || k === 'maxLoss'); } };
  const head = (k: SortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortKey === k} direction={asc ? 'asc' : 'desc'} onClick={() => toggleSort(k)}>{label}</TableSortLabel>
    </TableCell>
  );
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& tbody tr:hover': { bgcolor: 'action.hover' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 28 }} />
            {head('name', 'Strategy', 'left')}
            <TableCell sx={{ fontWeight: 700 }}>Shape</TableCell>
            {head('provenPnl', 'If proven')}
            {head('winProbability', 'Win%')}
            {head('expectedPnl', 'E[P&L]')}
            {head('maxProfit', 'Max P')}
            {head('maxLoss', 'Max L')}
            {head('marginEstimate', 'Margin')}
            <TableCell align="right" sx={{ fontWeight: 700 }}>Δ / θ / ν</TableCell>
            {head('fitScore', 'Fit')}
            <TableCell align="right" sx={{ fontWeight: 700 }}>Lots</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>Place</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((b, i) => {
            const isOpen = !!open[b.id + i];
            return (
              <React.Fragment key={b.id + i}>
                <TableRow>
                  <TableCell sx={{ p: 0.5 }}>
                    <IconButton size="small" onClick={() => setOpen(o => ({ ...o, [b.id + i]: !isOpen }))}>
                      {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{b.name}<Typography component="div" variant="caption" color="text.secondary">{b.category}</Typography></TableCell>
                  <TableCell><Chip size="small" color={shapeColor[b.riskShape]} label={b.riskShape} sx={{ height: 16, fontSize: '0.54rem', textTransform: 'capitalize' }} /></TableCell>
                  <TableCell align="right" sx={{ color: b.provenPnl >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>{fmtInr(b.provenPnl)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{b.winProbability}%</TableCell>
                  <TableCell align="right" sx={{ color: b.expectedPnl >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>{fmtInr(b.expectedPnl)}</TableCell>
                  <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmtInr(b.maxProfit)}</TableCell>
                  <TableCell align="right" sx={{ color: '#c62828' }}>{fmtInr(b.maxLoss)}</TableCell>
                  <TableCell align="right">{fmtInr(b.marginEstimate)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{b.greeks.delta} / {Math.round(b.greeks.theta)} / {Math.round(b.greeks.vega)}</TableCell>
                  <TableCell align="right"><Tooltip arrow title={FIT_HELP}><Chip size="small" label={b.fitScore} sx={{ height: 18, fontWeight: 800, bgcolor: fitColor(b.fitScore), color: '#fff', cursor: 'help' }} /></Tooltip></TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={lotsFor(b)} variant="standard" sx={{ width: 56 }} inputProps={{ min: 1, style: { textAlign: 'right' } }}
                      onChange={e => setLotsByRow(o => ({ ...o, [b.id]: Math.max(1, Math.floor(Number(e.target.value) || 1)) }))} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <ButtonGroup size="small" variant="outlined">
                      {onAdjust && <Tooltip arrow title="Adjust wing distance / push protective legs to a later expiry, then place."><span><Button onClick={() => onAdjust(b)}>✎</Button></span></Tooltip>}
                      {onSaveSymbol && <Tooltip arrow title="Save as a synthetic symbol (no order placed) — chart it and trigger it from the Charts symbol pane."><span><Button color="secondary" disabled={savingSym === b.id} onClick={() => onSaveSymbol(b)}>{savingSym === b.id ? '…' : '💾'}</Button></span></Tooltip>}
                      <Tooltip arrow title={PAPER_HELP}><span><Button color="info" disabled={placing === `${b.id}:PAPER` || placing === `${b.id}:LIVE`} onClick={() => onPlace(b, lotsFor(b), 'PAPER')}>{placing === `${b.id}:PAPER` ? '…' : 'Paper'}</Button></span></Tooltip>
                      <Tooltip arrow title={LIVE_HELP}><span><Button color="error" disabled={placing === `${b.id}:PAPER` || placing === `${b.id}:LIVE`} onClick={() => onPlace(b, lotsFor(b), 'LIVE')}>{placing === `${b.id}:LIVE` ? '…' : 'LIVE'}</Button></span></Tooltip>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={13} sx={{ py: 0, border: 0 }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 1.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', mb: 0.3 }}>Why this</Typography>
                          {b.reasoning.map((line, j) => <Typography key={j} variant="caption" sx={{ display: 'block', lineHeight: 1.5, color: j === 0 ? 'text.primary' : 'text.secondary' }}>{j === 0 ? '' : '• '}{line}</Typography>)}
                          {b.breakEvens.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Break-even: <strong>{b.breakEvens.map(x => x.toLocaleString('en-IN')).join(' / ')}</strong> · legs: {b.legs.map(l => `${l.action === 'BUY' ? '+' : '-'}${l.optionType}${l.strike}`).join(' ')}</Typography>}
                        </Box>
                        <PayoffChartPro basket={b as any} spotPrice={spot} underlyingSymbol={base} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default StrategyArchitect;
